import * as React from 'react';
import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

import { FormattedMessage } from '../../util/reactIntl';
import { Button } from '../ui/button';

import CookieCategoriesGrid from './CookieCategoriesGrid';
import { ConsentSource, useConsentForm } from './useConsentForm';

type Props = {
  source: ConsentSource;
  /** Loading skeleton rendered while the active policy resolves. */
  loadingFallback?: React.ReactNode;
};

const CookieConsentPanel = ({ source, loadingFallback = null }: Props) => {
  const form = useConsentForm(source);
  const [showSaved, setShowSaved] = useState(false);

  if (!form.isReady) return <>{loadingFallback}</>;

  const flashSaved = (ok: boolean) => {
    if (!ok) return;
    setShowSaved(true);
    window.setTimeout(() => setShowSaved(false), 2400);
  };

  const onAcceptAll = async () => flashSaved(await form.acceptAll());
  const onRejectAll = async () => flashSaved(await form.rejectAll());
  const onSave = async () => flashSaved(await form.saveCustom());

  return (
    <div className="space-y-5" data-testid="panel-cookie-consent-settings">
      <CookieCategoriesGrid draft={form.draft} onToggle={form.toggle} />

      {showSaved && (
        <div
          className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700"
          data-testid="status-cookie-consent-saved"
        >
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          <FormattedMessage id="CookieConsent.savedConfirmation" />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          className="h-9 px-3 text-xs font-heading uppercase tracking-[0.14em] text-[#5f584e]"
          onClick={onRejectAll}
          disabled={form.isSubmitting}
          data-testid="button-cookie-settings-reject-all"
        >
          <FormattedMessage id="CookieConsent.rejectAll" />
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9 border-[#d9cebf] bg-white px-3 text-xs font-heading uppercase tracking-[0.14em]"
          onClick={onAcceptAll}
          disabled={form.isSubmitting}
          data-testid="button-cookie-settings-accept-all"
        >
          <FormattedMessage id="CookieConsent.acceptAll" />
        </Button>
        <Button
          type="button"
          className="h-9 px-3 text-xs font-heading uppercase tracking-[0.14em]"
          onClick={onSave}
          disabled={form.isSubmitting}
          data-testid="button-cookie-settings-save"
        >
          <FormattedMessage id="CookieConsent.saveChoices" />
        </Button>
      </div>
    </div>
  );
};

export default CookieConsentPanel;
